
import React from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import DashboardOverview from '@/components/dashboard/DashboardOverview';
import MobileDashboardOverview from '@/components/dashboard/MobileDashboardOverview';
import UserSettingsTab from '@/components/dashboard/UserSettingsTab';
import AdminBehavioralTab from '@/components/AdminBehavioralTab';
import AdminProfilesTab from '@/components/AdminProfilesTab';
import AdminWorkBehaviorTab from '@/components/AdminWorkBehaviorTab';
import CompanyRegistrationTab from '@/components/company/CompanyRegistrationTab';
import PersonComparison from '@/pages/PersonComparison';
import ProfilesPage from '@/pages/ProfilesPage';
import ReferralPage from '@/components/ReferralPage';
import { IdealProfile } from '@/types/dashboardTypes';

interface DashboardContentProps {
  activeTab: string;
  totalCounts: {
    behavioral: number;
    idealProfiles: number;
  };
  // Behavioral tab props
  behavioralSubmissions: any[];
  workBehaviorSubmissions: any[];
  currentFilters: any;
  onFiltersChange: (filters: any) => void;
  filtersExpanded: boolean;
  onToggleFilters: () => void;
  onViewDetails: (submission: any) => void;
  onSubmissionDeleted: (id: string) => void;
  onStatusUpdate?: (submissionId: string, newStatus: string) => Promise<void>;
  behavioralPagination: any;
  workBehaviorPagination: any;
  filteredBehavioralCount: number;
  filteredWorkBehaviorCount: number;
  searchTerm: string;
  isSearching?: boolean;
  onSearchChange: (term: string) => void;
  tableExpanded: boolean;
  onToggleTable: () => void;
  selectedIdealProfile: IdealProfile | null;
  setSelectedIdealProfile: (profile: IdealProfile | null) => void;
  idealProfiles: any[];
  onDeleteProfile: (profile: IdealProfile) => void;
  deletingProfileIds: Set<string>;
  profilesPagination: any;
  profileFilter: string;
  onProfileFilterChange: (value: string) => void;
}

const DashboardContent: React.FC<DashboardContentProps> = ({
  activeTab,
  totalCounts,
  behavioralSubmissions,
  workBehaviorSubmissions,
  currentFilters,
  onFiltersChange,
  filtersExpanded,
  onToggleFilters,
  onViewDetails,
  onSubmissionDeleted,
  onStatusUpdate,
  behavioralPagination,
  workBehaviorPagination,
  filteredBehavioralCount,
  filteredWorkBehaviorCount,
  searchTerm,
  isSearching = false,
  onSearchChange,
  tableExpanded,
  onToggleTable,
  selectedIdealProfile,
  setSelectedIdealProfile,
  idealProfiles,
  onDeleteProfile,
  deletingProfileIds,
  profilesPagination,
  profileFilter,
  onProfileFilterChange
}) => {
  const isMobile = useIsMobile();

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        // Use mobile-optimized dashboard on mobile devices
        return isMobile ? (
          <MobileDashboardOverview 
            candidates={behavioralSubmissions}
            loading={false}
            onViewCandidate={onViewDetails}
          />
        ) : (
          <DashboardOverview totalCounts={totalCounts} />
        );
      case 'settings':
        return <UserSettingsTab />;
      case 'behavioral':
        return (
          <AdminBehavioralTab
            filteredSubmissions={behavioralSubmissions}
            allSubmissions={behavioralSubmissions}
            currentFilters={currentFilters}
            onFiltersChange={onFiltersChange}
            filtersExpanded={filtersExpanded}
            onToggleFilters={onToggleFilters}
            onViewDetails={onViewDetails}
            onSubmissionDeleted={onSubmissionDeleted}
            onStatusUpdate={onStatusUpdate}
            pagination={behavioralPagination}
            totalCount={filteredBehavioralCount}
            searchTerm={searchTerm}
            isSearching={isSearching}
            onSearchChange={onSearchChange}
            tableExpanded={tableExpanded}
            onToggleTable={onToggleTable}
            selectedIdealProfile={selectedIdealProfile}
            setSelectedIdealProfile={setSelectedIdealProfile}
            profileFilter={profileFilter}
            onProfileFilterChange={onProfileFilterChange}
          />
        );
      case 'profiles':
        return (
          <AdminProfilesTab
            idealProfiles={idealProfiles}
            onDeleteProfile={onDeleteProfile}
            deletingProfileIds={deletingProfileIds}
            pagination={profilesPagination}
            totalCount={totalCounts.idealProfiles}
          />
        );
      case 'registration':
        return <CompanyRegistrationTab />;
      case 'comparison':
        return <PersonComparison />;
      case 'workBehavior':
        return (
          <AdminWorkBehaviorTab
            filteredSubmissions={workBehaviorSubmissions}
            allSubmissions={workBehaviorSubmissions}
            currentFilters={currentFilters}
            onFiltersChange={onFiltersChange}
            filtersExpanded={filtersExpanded}
            onToggleFilters={onToggleFilters}
            onViewDetails={onViewDetails}
            onSubmissionDeleted={onSubmissionDeleted}
            onStatusUpdate={onStatusUpdate}
            pagination={workBehaviorPagination}
            totalCount={filteredWorkBehaviorCount}
            searchTerm={searchTerm}
            isSearching={isSearching}
            onSearchChange={onSearchChange}
            tableExpanded={tableExpanded}
            onToggleTable={onToggleTable}
            selectedIdealProfile={selectedIdealProfile}
            setSelectedIdealProfile={setSelectedIdealProfile}
          />
        );
      case 'userProfiles':
        return <ProfilesPage />;
      case 'referrals':
        return <ReferralPage />;
      default:
        return isMobile ? (
          <MobileDashboardOverview 
            candidates={behavioralSubmissions}
            loading={false}
            onViewCandidate={onViewDetails}
          />
        ) : (
          <DashboardOverview totalCounts={totalCounts} />
        );
    }
  };

  return (
    <div className={isMobile ? "p-0" : "py-6"}>
      <div className={isMobile ? "" : "max-w-7xl mx-auto px-6"}>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default DashboardContent;
